import type { Market } from '@ts-internal/shared-lib';
import { useTranslations } from '@vocab/react';
import {
  Box,
  Column,
  Columns,
  ContentBlock,
  Loader,
  Stack,
} from 'braid-design-system';
import { useEffect, useState } from 'react';

import { useAuthLinksWithReturnUrl } from 'src/App/services/urls/useAuthLinksWithReturnUrl';
import type { GetCandidateDetailsQuery } from 'src/graphql/Queries/getCandidateDetails.generated';

import { SearchLandingPageInfo } from '../SearchLandingPageInfoFooter/SearchLandingPageInfo';

import translations from './.vocab';
import { SearchLandingPageBanner } from './SearchLandingPageBanner';
import profileIllustrationEN from './images/profile_illustration_EN.png';
import profileIllustrationHK from './images/profile_illustration_HK.png';
import profileIllustrationID from './images/profile_illustration_ID.png';
import profileIllustrationTH from './images/profile_illustration_TH.png';

const PROFILE_UPDATE_URL = '/profile/me';
const VISIBILITY_SETTING_URL = '/profile/me/profile-visibility-v2?mode=edit';

const getProfileIllustration = (market: Market): string => {
  switch (market) {
    case 'HK':
      return profileIllustrationHK;
    case 'ID':
      return profileIllustrationID;
    case 'TH':
      return profileIllustrationTH;
    default:
      return profileIllustrationEN;
  }
};

export type SearchLandingPageProps = {
  isAuthenticated: boolean;
  candidateDetails: GetCandidateDetailsQuery | undefined;
  candidateDetailsLoading: boolean;
  domainMarket: Market;
};

export const SearchLandingPage = ({
  isAuthenticated,
  candidateDetails,
  candidateDetailsLoading,
  domainMarket,
}: SearchLandingPageProps) => {
  const { t } = useTranslations(translations);
  const { loginUrl } = useAuthLinksWithReturnUrl();

  // Track if client-side is ready (for authenticated users to avoid hydration mismatch)
  const [isClientReady, setIsClientReady] = useState(!isAuthenticated);

  useEffect(() => {
    if (isAuthenticated && !candidateDetailsLoading) {
      setIsClientReady(true);
    }
  }, [isAuthenticated, candidateDetailsLoading]);

  const isPublicProfile =
    candidateDetails?.viewer?.profileVisibility2?.id === 'Public';

  const renderBanner = () => {
    if (!isAuthenticated) {
      return (
        <SearchLandingPageBanner
          title={t('YOUR_PROFILE_CONNECTS_YOU_TO_YOUR_NEXT_OPPORTUNITY')}
          description={t(
            'TRUSTED_BY_EMPLOYERS_AND_BUILT_TO_CONNECT_YOU_TO_YOUR_NEXT_JOB',
          )}
          buttonText={t('CREATE_PROFILE')}
          href={loginUrl}
        />
      );
    }

    if (isPublicProfile) {
      return (
        <SearchLandingPageBanner
          title={t('MAKE_SURE_YOUR_PROFILE_WORKS_FOR_YOU')}
          description={t(
            'KEEP_YOUR_PROFILE_UP_TO_DATE_TO_APPEAR_IN_MORE_SEARCHES',
          )}
          buttonText={t('UPDATE_PROFILE')}
          href={PROFILE_UPDATE_URL}
        />
      );
    }

    return (
      <SearchLandingPageBanner
        title={t('WANT_TO_BE_DISCOVERED_FOR_THE_RIGHT_OPPORTUNITIES')}
        description={t('MAKE_YOUR_PROFILE_VISIBLE')}
        buttonText={t('SET_YOUR_PROFILE_VISIBILITY_TO_PUBLIC')}
        href={VISIBILITY_SETTING_URL}
      />
    );
  };

  return (
    <ContentBlock width="large">
      <Box
        paddingX="small"
        paddingY="large"
        paddingTop="xlarge"
        paddingBottom="xxxlarge"
      >
        {!isClientReady && (
          <Box
            display="flex"
            alignItems="center"
            justifyContent="center"
            style={{ minHeight: '40vh' }}
          >
            <Loader />
          </Box>
        )}
        {isClientReady && (
          <Stack space="xxlarge">
            <Columns space="xlarge" collapseBelow="tablet">
              <Column width="1/2">
                <Box
                  display="flex"
                  alignItems="center"
                  height="full"
                  paddingY={{ mobile: 'none', tablet: 'medium' }}
                >
                  <Stack space="medium">{renderBanner()}</Stack>
                </Box>
              </Column>
              <Column width="1/2">
                <Box display="flex" alignItems="center" justifyContent="center">
                  <img
                    src={getProfileIllustration(domainMarket)}
                    alt="Profile illustration"
                    style={{
                      maxWidth: '100%',
                      height: 'auto',
                    }}
                  />
                </Box>
              </Column>
            </Columns>
            <SearchLandingPageInfo />
          </Stack>
        )}
      </Box>
    </ContentBlock>
  );
};
